import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User } from '@/types';
import api, { apiUrl, refreshAccessToken } from '@/utils/api';
import { forgetPushOnSignOut, rememberedEndpoint } from '@/services/pushSubscription';
import { keepStorageForFieldWork, queueUserChanged } from '@/services/pendingQueue';
import { clearLockState } from '@/components/Tech/lockState';
import { dropHeldQueueToasts } from '@/services/queueToasts';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface LoginPayload {
  email: string;
  password: string;
  /** The authenticator's six digits, or a backup code (Employee rebuild B27).
   *  Sent as X-2FA-Code; the server asks for it with a 403 when it is needed. */
  twoFactorCode?: string;
}

interface LoginResponse {
  access_token: string;
  refresh_token: string;
  user: User;
}

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  // Actions
  login: (payload: LoginPayload) => Promise<void>;
  logout: () => void;
  refresh: () => Promise<void>;
  updateUser: (user: Partial<User>) => void;
  setTokens: (accessToken: string, refreshToken: string) => void;
  setUser: (user: User) => void;
  setLoading: (loading: boolean) => void;
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,

      login: async (payload: LoginPayload) => {
        set({ isLoading: true });
        try {
          // Step 1: Authenticate and get tokens
          const { twoFactorCode, ...credentials } = payload;
          const response = await api.post<{ success: boolean; data: { access_token: string; refresh_token: string; token_type: string; expires_in: number }; message: string }>(
            '/auth/login', credentials,
            twoFactorCode ? { headers: { 'X-2FA-Code': twoFactorCode } } : undefined,
          );
          const { access_token, refresh_token } = response.data.data;
          set({
            accessToken: access_token,
            refreshToken: refresh_token,
            isAuthenticated: true,
          });

          // Step 2: Fetch user profile using the new token
          try {
            const meResponse = await api.get<{ success: boolean; data: User; message: string }>('/auth/me', {
              headers: { Authorization: `Bearer ${access_token}` },
            });
            const u = meResponse.data.data;
            set({ user: u, isLoading: false });
            // A fresh password sign-in is never met by the PIN lock of an
            // earlier sign-in, however long ago its last touch was.
            clearLockState(u.id);
            // FM Technician A16: the unsent-work lists shown are now this
            // person's, and a field worker's device keeps them through a
            // storage clear-out.
            queueUserChanged();
            keepStorageForFieldWork(u.permissions);
            // Tag Sentry events with user identity (no email by default — send_default_pii is off backend-side too)
            try {
              const Sentry = await import('@sentry/react');
              Sentry.setUser({ id: u.id, username: `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() });
              Sentry.setTag('org_id', u.organisation_id);
              Sentry.setTag('user_role', String(u.role || 'unknown'));
            } catch { /* sentry optional */ }
          } catch {
            // Even if /me fails, login succeeded — user can still navigate
            set({ isLoading: false });
          }
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      logout: () => {
        // 🔴 SIGN-OUT HAS TO REACH THE SERVER (Deep-test DSH-041). This used to
        // go through `api`, whose request interceptor reads the access token
        // from storage when the request is SENT — by which time the `set`
        // below had already cleared it. Every sign-out therefore went out with
        // no Authorization header, was answered 401, and revoked nothing; the
        // `.catch` hid it. Worse, a 401 on `api` starts the refresh-and-
        // redirect flow.
        //
        // The refresh token is read NOW, before anything is cleared, and is all
        // the server needs: holding it is the authority for /auth/logout, as it
        // is for /auth/refresh. A bare `fetch` keeps this out of the 401
        // interceptor, and `keepalive` lets it finish when the sign-out is
        // followed by a full-page navigation to /login.
        const token = get().refreshToken;
        // Employee rebuild B24: this device's push subscription goes with the
        // sign-in, so a shared or handed-back device stops receiving the
        // previous person's reminders. The endpoint rides on the logout call
        // (the refresh token is the authority there too), read synchronously
        // from where turnOnPush remembered it.
        const pushEndpoint = rememberedEndpoint();
        if (token) {
          try {
            fetch(apiUrl('/api/auth/logout'), {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ refresh_token: token, push_endpoint: pushEndpoint ?? undefined }),
              keepalive: true,
            }).catch(() => { /* offline: the session expires on its own */ });
          } catch { /* no fetch (very old browser): nothing more to do */ }
        }
        if (pushEndpoint) forgetPushOnSignOut();
        // FM Technician B18 (D8): this person's offline pack and saved manuals
        // go with the sign-in. Their unsent queue is NOT touched — it waits
        // for them under their own id (pendingQueue).
        const signingOut = get().user?.id ?? null;
        // Review R2-3 #6: the queue toasts held under the lock are this
        // person's — dropped BEFORE the lock is cleared below (a lock change
        // would otherwise show them on the login screen, or to the next
        // person). The blocked list keeps every refusal.
        dropHeldQueueToasts();
        if (signingOut) {
          void import('@/services/offlineSync').then((m) => m.clearOfflineFor(signingOut)).catch(() => undefined);
          // The PIN lock and the last touch go with the sign-in (lockState).
          clearLockState(signingOut);
        }
        // Clear Sentry user context
        import('@sentry/react').then((S) => S.setUser(null)).catch(() => {});
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          isLoading: false,
        });
        // A16: nobody's lists are shown once signed out. The unsent items stay
        // in storage under their owner's id for when they sign in again.
        queueUserChanged();
      },

      // Through the ONE refresh (utils/api.ts refreshAccessToken), never a
      // second POST of its own: two refreshes with the same token cannot both
      // succeed, and the loser signs the person out (Deep-test EXT-23).
      refresh: async () => {
        if (!get().refreshToken) {
          get().logout();
          return;
        }
        try {
          await refreshAccessToken();
        } catch {
          get().logout();
        }
      },

      updateUser: (partial: Partial<User>) => {
        const currentUser = get().user;
        if (currentUser) {
          set({ user: { ...currentUser, ...partial } });
        }
      },

      setTokens: (accessToken: string, refreshToken: string) => {
        set({ accessToken, refreshToken, isAuthenticated: true });
      },

      setUser: (user: User) => {
        set({ user, isAuthenticated: true });
      },

      setLoading: (loading: boolean) => {
        set({ isLoading: loading });
      },
    }),
    {
      name: 'spaciora-auth',
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    },
  ),
);
